import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { BuyRequest, IRogue } from '@/js/services/RogueTypes'
import { useProductStore } from '@/js/stores/productStore'

export function getTallestUnitId(buyRequest: BuyRequest, rogue: IRogue): number | undefined {
  // Filter valid unit-level BRs
  const bundleBRs = buyRequest.children?.filter(br => !!br.metadata?.id)

  if (!bundleBRs) return undefined
  if (bundleBRs.length === 1) return bundleBRs[0]?.metadata?.id

  // Find the tallest unit's height and ID
  const maxHeight = Math.max(...bundleBRs.map(br => Number(getSimpleForBuyRequest(br, rogue)?.attributes?.mass_height?.toString().replace('"', ''))))
  // First rather than last tallest keeps the Pair on the base while same-height units are added, instead of hopping to each new one.
  const tallestUnit = bundleBRs.find(br => Number(getSimpleForBuyRequest(br, rogue)?.attributes?.mass_height?.toString().replace('"', '')) === maxHeight)

  const tallestUnitId = tallestUnit?.metadata?.id

  return tallestUnitId
}

// Each non-Pair unit's panel is the upright on its side away from the Pair, which the 3D draws at the taller of the two units it joins (ms-addon-utils sharedHeight); raises only the panel, so call it on a send-time clone, never on the page's buy request.
export function raiseShortKitPanels(buyRequest: BuyRequest, rogue: IRogue): void {
  const units = buyRequest.children?.filter(br => !!br.metadata?.id) ?? []
  const pairId = getTallestUnitId(buyRequest, rogue)
  const pairIndex = units.findIndex(br => br.metadata?.id === pairId)

  if (pairIndex === -1) return

  const productStore = useProductStore(rogue.pinia)
  const toHeight = (val: unknown) => Number(String(val ?? '').replace(/"/g, ''))
  // Read before any swap so a raised panel never feeds the next unit's comparison.
  const heights = units.map(unit => toHeight(getSimpleForBuyRequest(unit, rogue)?.attributes?.mass_height))

  units.forEach((unit, i) => {
    if (i === pairIndex) return

    const required = heights[i < pairIndex ? i - 1 : i + 1]

    if (required === undefined || !(required > heights[i])) return

    const rackBR = unit.children?.[0]
    const panelBR = rackBR?.children?.[0]
    const panel = panelBR ? productStore.products[panelBR.productId] : undefined
    const sibling = (rackBR ? productStore.products[rackBR.productId]?.childrenIds ?? [] : [])
      .map(id => productStore.products[id])
      .find(child => !!child
        && toHeight(child.attributes?.mass_height) === required
        && child.attributes?.mass_width === panel?.attributes?.mass_width
        && child.attributes?.edition === panel?.attributes?.edition)

    if (!panelBR || !sibling) {
      rogue.log.warn(`Mass Storage unit ${unit.metadata?.id}: no ${required}" panel matching ${panel?.sku}, kit left short`)

      return
    }

    panelBR.productId = sibling.productId
  })
}
